// 📄 pages/SAP-BTP/CloudIdentityHome.tsx
// Vista dedicada de Cloud Identity Service, accedida desde la tarjeta
// homónima en BTPHome.tsx. Mismo patrón de navegación por estado (`page`,
// ver App.js/OperativeApp) y mismo layout que IntegrationSuiteHome.tsx.
import React from 'react';
import { UserCheck, Users, KeyRound, UserPlus, ShieldCheck, Plus } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';

interface CloudIdentityHomeProps {
  setPage: (page: string) => void;
}

interface ToolCardProps {
  title: string;
  desc: string;
  icon: React.ReactNode;
}

// 2026-08-20: cambiado de `slate-*` (paleta de Tailwind sin remapear a las
// variables de tema, ver tailwind.config.js — a diferencia de white/gray/
// zinc, que sí responden al toggle claro/oscuro) a `gray-*`/`zinc-*`, que
// sí lo hacen. `cyan-*` es el acento de esta sección y queda fijo a
// propósito, mismo criterio que el resto de la app.
const ToolCard: React.FC<ToolCardProps> = ({ title, desc, icon }) => (
  <div className="group bg-zinc-900 border border-zinc-800 rounded-2xl p-8 transition-all duration-300 hover:border-cyan-500/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,180,216,0.1)]">
    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 bg-cyan-500/10 border border-cyan-500/20 group-hover:bg-cyan-500/15 group-hover:border-cyan-400/30 transition-all">
      {icon}
    </div>
    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors">{title}</h3>
    <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
  </div>
);

export default function CloudIdentityHome({ setPage }: CloudIdentityHomeProps) {
  return (
    <main className="min-h-screen bg-gray-900 text-white p-8">
      <div className="relative z-10 mb-8">
        <Breadcrumbs currentPage="btp-cloud-identity" setPage={setPage} />
      </div>

      <header className="flex flex-col items-center text-center mb-16 mt-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br from-cyan-500 to-emerald-500 shadow-lg shadow-cyan-900/40">
          <UserCheck className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Cloud Identity Service</h1>
        <p className="text-gray-400 max-w-2xl text-base font-light italic">
          Gestión de identidades, autenticación SSO, roles y accesos para todo el ecosistema SAP.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        <ToolCard
          title="Gestión de Usuarios"
          desc="Alta, baja y administración de usuarios, grupos y atributos del directorio del ecosistema SAP."
          icon={<Users className="w-6 h-6 text-cyan-400" />}
        />
        <ToolCard
          title="Integración SSO"
          desc="Configuración de Single Sign-On (SAML2, OIDC) entre aplicaciones cloud y on-premise."
          icon={<KeyRound className="w-6 h-6 text-cyan-400" />}
        />
        <ToolCard
          title="Aprovisionamiento"
          desc="Sincronización automática de identidades entre sistemas mediante SCIM y conectores de aprovisionamiento."
          icon={<UserPlus className="w-6 h-6 text-cyan-400" />}
        />
        <ToolCard
          title="Roles y Permisos"
          desc="Definición de roles de negocio, grupos de autorización y políticas de acceso por aplicación."
          icon={<ShieldCheck className="w-6 h-6 text-cyan-400" />}
        />

        <div className="flex flex-col items-center justify-center text-center p-8 rounded-2xl border-2 border-dashed border-gray-600 text-gray-500 min-h-[180px]">
          <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center mb-3">
            <Plus className="w-5 h-5 text-cyan-400" />
          </div>
          <p className="text-sm">Más herramientas próximamente</p>
        </div>
      </div>
    </main>
  );
}
